import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency } from '../context/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Cart = () => {
  const { formatPrice } = useCurrency();
  const [cart, setCart] = useState({ items: [] });
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCart = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/cart`, { withCredentials: true });
      setCart(data);

      const productIds = data.items?.map((item) => item.product_id) || [];
      if (productIds.length > 0) {
        const productsData = await axios.get(`${BACKEND_URL}/api/products`);
        const productsMap = {};
        productsData.data.forEach((p) => {
          productsMap[p.id] = p;
        });
        setProducts(productsMap);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity <= 0) {
      await removeItem(productId);
      return;
    }

    const previousItems = cart.items;
    const updatedItems = previousItems.map((item) =>
      item.product_id === productId ? { ...item, quantity: newQuantity } : item
    );
    setCart({ ...cart, items: updatedItems });

    try {
      await axios.put(
        `${BACKEND_URL}/api/cart/${productId}`,
        { product_id: productId, quantity: newQuantity },
        { withCredentials: true }
      );
    } catch (error) {
      setCart({ ...cart, items: previousItems });
      if (error.response?.status === 401) {
        toast.error('Your session expired. Please log in again.');
        navigate('/login');
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to update quantity');
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/cart/${productId}`, { withCredentials: true });
      toast.success('Item removed from cart');
      fetchCart();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Your session expired. Please log in again.');
        navigate('/login');
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to remove item');
    }
  };

  const calculateTotal = () => {
    return cart.items?.reduce((total, item) => {
      const product = products[item.product_id];
      return total + (product ? product.price * item.quantity : 0);
    }, 0) || 0;
  };

  if (loading) {
    return (
      <div className="heritage-page min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c79a35]"></div>
      </div>
    );
  }

  return (
    <div className="heritage-page min-h-screen flex flex-col">
      <Header />

      <main className="heritage-shell flex-1">
        <h1 className="heritage-title mb-8 text-5xl" data-testid="cart-title">Shopping Cart</h1>

        {!cart.items || cart.items.length === 0 ? (
          <div className="text-center py-12" data-testid="empty-cart">
            <p className="text-[#6a5448] text-lg mb-4">Your cart is empty</p>
            <Button onClick={() => navigate('/products')} className="heritage-button rounded-none" data-testid="continue-shopping-btn">
              Continue Shopping
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => {
                const product = products[item.product_id];
                if (!product) return null;

                return (
                  <div key={item.product_id} className="heritage-card rounded-sm p-4 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                    <img src={product.image_urls?.[0] || product.image_url} alt={product.name} className="w-24 h-24 object-cover rounded-sm" />
                    <div className="flex-1">
                      <h3 className="text-lg font-heading">{product.name}</h3>
                      <p className="text-sm text-[#6a5448]">{product.category}</p>
                      <p className="heritage-title mt-2 text-2xl">{formatPrice(product.price)}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(item.product_id)}
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-[#8C1B2F]" />
                      </Button>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="h-8 w-8"
                          data-testid={`decrease-qty-${item.product_id}`}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="w-8 text-center" data-testid={`quantity-${item.product_id}`}>{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="h-8 w-8"
                          data-testid={`increase-qty-${item.product_id}`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="lg:col-span-1">
              <div className="heritage-card sticky top-24 rounded-sm p-6" data-testid="cart-summary">
                <h2 className="heritage-title mb-6 text-3xl">Order Summary</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-[#6a5448]">Subtotal</span>
                    <span data-testid="cart-subtotal">{formatPrice(calculateTotal())}</span>
                  </div>
                  <div className="flex justify-between font-heading text-xl">
                    <span>Total</span>
                    <span className="text-[#4b2e24]" data-testid="cart-total">{formatPrice(calculateTotal())}</span>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/checkout')}
                  className="heritage-button w-full rounded-none"
                  data-testid="proceed-to-checkout-btn"
                >
                  Proceed to Checkout
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Cart;
